import { Link } from "react-router-dom";

/**
 * The frame every fully-navigable operations list shares: a title, a deck, the way back,
 * a filter row, the count, and the states an empty result can be in.
 *
 * **A filtered list that matched nothing, a table that has never had a row, and a search
 * whose term was too broad are three different states that all render as an empty list**,
 * and each wants a different response. Left to each page, one would render a blank.
 */
export function OpsHistoryShell({
  title,
  deck,
  filters,
  count,
  note,
  isPending,
  error,
  isEmpty,
  emptyFiltered,
  emptyAll,
  children,
  onLoadMore,
  loadingMore,
}: {
  title: string;
  deck: string;
  filters: React.ReactNode;
  count: React.ReactNode;
  note?: React.ReactNode;
  isPending: boolean;
  error: Error | null;
  isEmpty: boolean;
  /** True when any filter is set, which decides which empty sentence is honest. */
  emptyFiltered: boolean;
  emptyAll: string;
  children: React.ReactNode;
  onLoadMore: (() => void) | null;
  loadingMore: boolean;
}) {
  return (
    /*
      The same frame the Operations page uses: `.read` scrolls, `.article` sets the
      measure and the gutter, so a list does not run to the viewport's edge and read as
      clipped.
    */
    <div className="read">
      <article className="article opshistory">
      <div className="panehd">
        <div>
          <h1>{title}</h1>
          <p className="deck">{deck}</p>
        </div>
        <Link className="iconbtn" to="/ops">
          ← Operations
        </Link>
      </div>

      {filters}

      {isPending && <p className="deck">Loading…</p>}
      {error && <p className="err">{error.message}</p>}

      {!isPending && !error && (
        <>
          <p className="fin-caveat">
            {count}
            {note}
          </p>
          <div className="filings live">{children}</div>
          {isEmpty && (
            <p className="deck">{emptyFiltered ? "Nothing matches those filters." : emptyAll}</p>
          )}
          {onLoadMore && (
            <button type="button" className="iconbtn" disabled={loadingMore} onClick={onLoadMore}>
              {loadingMore ? "Loading…" : "Load more"}
            </button>
          )}
        </>
      )}
      </article>
    </div>
  );
}
